import { getCookie } from '@pegasus/utils';
import { isClient, TrackingMode, TrackingModeCookie } from './lib/utils';
import { isConsentUpdateEvent } from './utils';
/**
 * An `InternalQueue` class is a class meant to be used internally by the `Tracker`.
 * It is used to queue events that are supposed to be tracked
 * but are still waiting for their matching condition to be met.
 */
export class InternalQueue {
    constructor(trackCallback) {
        this.trackCallback = trackCallback;
        this.queue = new Set();
        this.isConsentTracked = false;
        this.registerDataLayerConsentListener = () => {
            // eslint-disable-next-line no-restricted-properties
            const isConsentEventAvailable = window.dataLayer.find(isConsentUpdateEvent);
            if (isConsentEventAvailable) {
                this.isConsentTracked = true;
            }
            else {
                // eslint-disable-next-line no-restricted-properties
                const oldPush = window.dataLayer.push;
                // eslint-disable-next-line no-restricted-properties
                window.dataLayer.push = (...args) => {
                    // eslint-disable-next-line no-restricted-properties
                    const result = oldPush.apply(window.dataLayer, args);
                    if (!this.isConsentTracked && isConsentUpdateEvent(args[0])) {
                        this.isConsentTracked = true;
                        this.consumeQueueEntriesMatchingEvent(args[0], args[1]);
                    }
                    return result;
                };
            }
        };
        this.addToQueue = (event, payload, triggerCondition, queueConfig = {}) => {
            const queueEntry = [event, payload, triggerCondition];
            this.queue.add(queueEntry);
            if (queueConfig.flushTimeout) {
                // Is it safe to keep the timeout this way without a way to clear it?
                setTimeout(() => {
                    if (this.queue.has(queueEntry)) {
                        this.queue.delete(queueEntry);
                        this.trackCallback(event, payload);
                    }
                }, queueConfig.flushTimeout);
            }
        };
        this.consumeQueueEntriesMatchingEvent = (event, payload) => {
            if (this.queue.size === 0) {
                return undefined;
            }
            for (const queueIterator of this.queue) {
                const [queuedEvent, queuedPayload, queuedMatcher] = queueIterator;
                if (event !== queuedEvent) {
                    const isCurrentEventMatchingQueue = queuedMatcher({ event, ...payload });
                    if (isCurrentEventMatchingQueue) {
                        this.queue.delete(queueIterator);
                        this.trackCallback(queuedEvent, queuedPayload);
                        this.consumeQueueEntriesMatchingEvent(queuedEvent, queuedPayload);
                    }
                }
            }
        };
        this.flushQueue = () => {
            if (this.queue.size === 0) {
                return undefined;
            }
            if (process.env.NODE_ENV === 'development') {
                // eslint-disable-next-line no-console
                console.error(`[@pegasus/tracker] Flushing the internal queue of events. ` +
                    `${this.queue.size} events were still in queue. Pleas update their logic to make sure they are tracked properly ` +
                    `and do not depend on the condition that is unlikely to be met.`, Array.from(this.queue));
            }
            for (const queueIterator of this.queue) {
                const [queuedEvent, queuedPayload] = queueIterator;
                this.queue.delete(queueIterator);
                this.trackCallback(queuedEvent, queuedPayload);
            }
        };
        if (!this.trackCallback) {
            throw new Error('[@pegasus/tracker] trackCallback function is required.');
        }
        if (isClient()) {
            // eslint-disable-next-line no-restricted-properties
            window.dataLayer = window.dataLayer || [];
            /**
             * When the tracking mode cookie is set for `in_web`,
             * we can assume that the consent has been tracked, as it was set by the app.
             *
             * We can also use that cookie for the staging / upcoming environment.
             */
            this.isConsentTracked =
                process.env.NODE_ENV === 'development' ||
                    getCookie(TrackingModeCookie) === TrackingMode.InWeb;
            window.addEventListener('beforeunload', this.flushQueue);
            if (!this.isConsentTracked) {
                this.registerDataLayerConsentListener();
            }
        }
    }
}
//# sourceMappingURL=internal-queue.js.map